import React, { useRef, useEffect } from 'react';
import { Chart, registerables, ChartData as ChartJSData } from 'chart.js';
import { Box, Card, CardContent, Typography } from '@material-ui/core';

Chart.register(...registerables);

export interface ChartDataset {
  label: string;
  data: number[];
  fill?: boolean;
  borderColor?: string;
  backgroundColor?: string;
  tension?: number;
}

export interface ChartData extends ChartJSData<'line'> {
  labels: string[];
  datasets: ChartDataset[];
}

interface CustomChartProps {
  title: string;
  chartData: ChartData;
  borderColor?: string;
  backgroundColor?: string;
  height?: string;
}

const ChartCard: React.FC<CustomChartProps> = ({
  title,
  chartData,
  borderColor = '#000',
  backgroundColor = '#fff',
  height = '300px'
}) => {
  const chartRef = useRef<HTMLCanvasElement>(null);
  const chartInstance = useRef<Chart | null>(null);

  useEffect(() => {
    if (chartRef.current) {
      if (chartInstance.current) {
        chartInstance.current.destroy();
      }

      chartInstance.current = new Chart(chartRef.current, {
        type: 'line',
        data: {
          ...chartData,
          datasets: chartData.datasets.map(dataset => ({
            ...dataset,
            borderColor: borderColor || dataset.borderColor,
            backgroundColor: backgroundColor || dataset.backgroundColor,
          }))
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          scales: {
            y: {
              beginAtZero: true,
              ticks: {
                font: {
                  size: 15
                }
              }
            }
          },
          plugins: {
            legend: {
              display: false
            }
          }
        }
      });
    }

    return () => {
      if (chartInstance.current) {
        chartInstance.current.destroy();
      }
    };
  }, [chartData, borderColor, backgroundColor]);

  return (
    <Card sx={{ borderRadius: '16px', height: height, width: '100%' }}>
      <CardContent sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
        <Box mb={2}>
          <Typography variant="h6">{title}</Typography>
        </Box>
        <Box sx={{ flexGrow: 1, width: '100%', overflow: 'hidden' }}>
          <canvas ref={chartRef} style={{ width: '100%', height: '100%' }} />
        </Box>
      </CardContent>
    </Card>
  );
};

export default ChartCard;
